import React, { useState, useEffect, useRef, useContext } from 'react';

import ContentEditable from 'react-contenteditable';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import EditConfirmIcon from '@components/common/EditConfirmIcon/EditConfirmIcon';
import EditIcon from '@components/common/EditIcon/EditIcon';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import Text from '@components/common/Text/Text';
import { M_DEVICE } from '@components/lpc/Constants';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import CircleAvatar from '@components/lpc/Navbar/CircleAvatar/CircleAvatar';
import ContactModal from '@components/lpc/Pot/AmountModule/ContactOrganiser/ContactForm/ContactForm';
import Activity from '@components/lpc/Pot/MainModule/Activity/Activity';
import Participants from '@components/lpc/Pot/MainModule/Activity/Participants/Participants';
import GreetingCard from '@components/lpc/Pot/MainModule/Informations/GreetingCard/GreetingCard';
import Informations from '@components/lpc/Pot/MainModule/Informations/Informations';
import PotImageUpload from '@components/lpc/Pot/MainModule/Informations/PotImageUpload/PotImageUpload';
import { personRoles } from '@models/PotModel/personRoles';
import { potType } from '@propTypes/potTypes';
import potService from '@services/domain/Lpc/PotService';
import imageService from '@services/utils/ImageService';
import { htmlToText } from '@services/utils/StringService';
import { getUserAvatarSrc } from '@services/utils/UserService';

import css from './MainModule.module.scss';

type MainModuleProps = {
    pot: potType;
    mobileView?: boolean;
    personRole: string;
    goToParticipant: boolean;
    newPoolObjective?: (potObjectiveType) => void;
    onGoToParticipantsList: () => void;
    setOpenParticipate: (boolean) => void;
    openSetting?: boolean;
};

const MainModule = ({
    pot,
    personRole,
    mobileView,
    goToParticipant,
    onGoToParticipantsList,
    setOpenParticipate,
}: MainModuleProps) => {
    const { width } = useWindowSize();
    const { t } = useTranslateLpc(['lpc-create-pot', 'common', 'lpc-pot', 'lpc-pot-history']);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isContactOrganizerModalOpen, setIsContactOrganizerModalOpen] = useState<boolean>(false);
    const contentEditableRef = useRef(null);
    const [organizerName, setOrganizerName] = useState(pot.organizer.organizerName);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const isMobile = width <= M_DEVICE;
    const { addToast } = useToasts();
    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;

    const setCursorPosition = (element, position) => {
        const range = document.createRange();
        const selection = window?.getSelection();
        range.setStart(element.childNodes[0], position);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    useEffect(() => {
        if (isEditing && contentEditableRef.current) {
            // Set cursor position to the end of the content
            const textLength = contentEditableRef.current.innerText.length;
            setCursorPosition(contentEditableRef.current, textLength);
        }
    }, [isEditing]);

    useEffect(() => {
        onGoToParticipantsList();
    }, [goToParticipant, onGoToParticipantsList]);

    const onSubmit = () => {
        setIsLoading(true);
        if (organizerName != pot.organizer.organizerName && organizerName != '') {
            potService
                .updateOrganiser(pot.uniq, { organiser: htmlToText(organizerName) })
                .then((res) => {
                    if (res.status === 200) {
                        getUpdatedPot(pot.uniq);
                        setIsEditing(false);
                        htmlToText(organizerName) !== pot.organizer.organizerName &&
                            addToast(t(`success.ORGA_UPDATED`), {
                                appearance: 'success',
                                autoDismiss: true,
                            });
                        setIsLoading(false);
                    }
                })
                .catch(() =>
                    addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                        appearance: 'error',
                        autoDismiss: true,
                    }),
                );
        } else {
            setIsEditing(false);
            setIsLoading(false);
            setOrganizerName(pot.organizer.organizerName);
        }
    };

    const onEdit = () => {
        setIsEditing(true);
    };
    return (
        <>
            {!mobileView && (
                <PotImageUpload
                    personRole={personRole}
                    image={imageService.getPotImage(pot)}
                    potRef={pot.uniq}
                    potStatus={pot.status}
                    hidePoolImage={pot.hidePoolImage}
                    category={pot.category}
                    isOffered={pot.isOffered}
                    mobileView={mobileView}
                />
            )}

            {
                <div className={css.top__bottom}>
                    {
                        <>
                            <div className={css.top__organizer}>
                                <div>
                                    <CircleAvatar
                                        customClass={css.top__organizer__avatar}
                                        avatar={getUserAvatarSrc(pot.organizer)}
                                        width={'26px'}
                                        height={'26px'}
                                    />
                                </div>
                                <div className={css.top__organizer__text}>
                                    <div className={css.top__organizer__text__1}>
                                        {t('amount.contact.organised-by')}
                                    </div>
                                    {isEditing ? (
                                        <>
                                            <div className={css.top__organizer__text__editable}>
                                                <ContentEditable
                                                    innerRef={contentEditableRef}
                                                    className={`${css.top__organizer} ${
                                                        isEditing && css.top__organizer__editable
                                                    }`}
                                                    style={{ outline: 'none' }}
                                                    html={organizerName}
                                                    onChange={(e) => setOrganizerName(e.target.value)}
                                                    onFocus={() => setIsEditing(true)}
                                                />
                                            </div>
                                            <EditConfirmIcon
                                                customContainer={css.top__organizer__edit_container}
                                                customIcon={css.top__organizer__edit_confirm_icon}
                                                isLoading={isLoading}
                                                onClose={() => {
                                                    setOrganizerName(pot.organizer.organizerName);
                                                    setIsEditing(false);
                                                }}
                                                onConfirm={onSubmit}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Text
                                                variant={'caption_02'}
                                                color={'txt-primary'}
                                                customClass={css.top__organizer__text__editable}
                                            >
                                                {organizerName}
                                            </Text>

                                            {pot.personRole === personRoles.ORGANISER && (
                                                <EditIcon
                                                    onEdit={onEdit}
                                                    customBtnClass={css.top__organizer__edit_icon}
                                                />
                                            )}

                                            {pot.personRole !== personRoles.ORGANISER && (
                                                <>
                                                    <Button
                                                        onClick={() => setIsContactOrganizerModalOpen(true)}
                                                        customClass={css.contact__cta}
                                                        variant={'secondary'}
                                                        type={'button'}
                                                        size={'small'}
                                                    >
                                                        {t('amount.contact.contact')}
                                                    </Button>
                                                    <ContactModal
                                                        potRef={pot.uniq}
                                                        isVisible={isContactOrganizerModalOpen}
                                                        onHide={() => setIsContactOrganizerModalOpen(false)}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    }
                </div>
            }

            <section className={css.informations}>
                <Informations
                    description={pot.desc}
                    personRole={personRole}
                    potRef={pot.uniq}
                    potLink={pot.absoluteUrl}
                    category={pot.category}
                    solidaryCategory={pot.solidaryCategory}
                    eventDate={pot.eventDate}
                    isOffered={pot.isOffered}
                />
            </section>
            <div className={css.one_container}>
                <Activity potRef={pot.uniq} personRole={personRole} category={pot.category} isOffered={pot.isOffered} />
            </div>
            <div className={css.one_container}>
                <GreetingCard
                    potRef={pot.uniq}
                    messagesCount={pot.messagesCount}
                    hasMessage={pot.personParticipationInfo.hasLeftAMessage}
                    personRole={personRole}
                    potCategory={pot.category}
                    isOffered={pot.isOffered}
                />
            </div>
            {(pot.privacy.showParticipantsList || pot.personRole === personRoles.ORGANISER) && isMobile && (
                <div className={css.one_container}>
                    <Participants
                        pot={pot}
                        participationNumber={pot.participationNumber}
                        personRole={personRole}
                        potRef={pot.uniq}
                        privacy={pot.privacy}
                        poolContribution={pot.poolContribution}
                        setOpenParticipate={(value) => setOpenParticipate(value)}
                    />
                </div>
            )}
        </>
    );
};
export default MainModule;
